import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, LogIn, Eye, Download, Send, Truck, Mail, Fingerprint, CreditCard, PenLine, Link2, Video, FileText } from "lucide-react";
import { VideoEvidence } from "./VideoEvidence";
import { api, shortDate } from "../lib/api";
import { NeonButton } from "./common";

export const SOURCES = { payment_auth: "Processor API", device_fingerprint: "Checkout SDK", email_confirmation: "Email server", login: "App auth webhook", content_access: "App event webhook", file_download: "CDN access log", submission: "App event webhook", delivery: "Fulfillment webhook", consent: "Consent capture", video_verification: "Buyer video verification", video_transcript: "Whisper transcript" };

export const ICONS = {
  login: LogIn, content_access: Eye, file_download: Download, submission: Send, delivery: Truck,
  email_confirmation: Mail, device_fingerprint: Fingerprint, payment_auth: CreditCard, consent: PenLine, video_verification: Video, video_transcript: FileText,
};

const Node = ({ e }) => {
  const [v, setV] = useState(null);
  const Icon = ICONS[e.type] || ShieldCheck;
  const verify = async () => {
    const { data } = await api.get(`/evidence/${e.id}/verify`);
    setV(data.valid);
  };
  return (
    <li className="relative pl-10 pb-6 group" data-testid={`evidence-node-${e.id}`}>
      <span className="absolute left-0 top-0 w-7 h-7 rounded-sm border border-[#24302E] bg-field flex items-center justify-center transition-[border-color,box-shadow] duration-200 group-hover:border-brand group-hover:shadow-[0_0_12px_var(--brand)]">
        <Icon className="w-3.5 h-3.5 text-brand" strokeWidth={1.5} />
      </span>
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-wider text-accent2">{e.type.replaceAll("_", " ")}</p>
        <span className="font-mono text-[10px] text-white/35">{shortDate(e.timestamp)}</span>
      </div>
      <p className="text-sm mt-1 text-[#DDD]">{e.description}</p>
      {e.type === "video_verification" && e.metadata?.verification_id && <VideoEvidence vid={e.metadata.verification_id} testid={`timeline-video-${e.id}`} />}
      <div className="flex items-center gap-3 mt-2">
        <code className="text-[10px] text-white/30 truncate" title="content address">cid sha256:{e.hash.slice(0, 24)}… · {SOURCES[e.type] || "Platform"}{e.metadata?.ip ? ` · ${e.metadata.ip}` : ""}</code>
        <button onClick={verify} data-testid={`verify-evidence-${e.id}`}
          className={`font-mono text-[10px] uppercase tracking-wider shrink-0 transition-colors ${v === null ? "text-white/45 hover:text-brand" : v ? "text-brand" : "text-[#F87171]"}`}>
          {v === null ? "verify" : v ? "✓ signature valid" : "✗ tampered"}
        </button>
      </div>
    </li>
  );
};

export const EvidenceTimeline = ({ evidence, transactionId }) => {
  const [chain, setChain] = useState(null);
  const verifyChain = async () => {
    const { data } = await api.get(`/evidence/chain/${transactionId}`);
    setChain(data);
    data.valid ? toast.success(`Chain intact · ${data.records} records`) : toast.error("Chain integrity broken");
  };
  return (
    <div data-testid="evidence-timeline">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-white/45">{evidence.length} signed records · hash-chained</p>
        <NeonButton variant="ghost" onClick={verifyChain} data-testid="verify-chain-btn" className="!py-1.5">
          <Link2 className="w-3 h-3" /> Verify chain
        </NeonButton>
      </div>
      {chain && (
        <div className={`mb-5 p-3 border rounded-sm font-mono text-[11px] ${chain.valid ? "border-brand/30 text-brand" : "border-[#F87171]/40 text-[#F87171]"}`} data-testid="chain-result">
          {chain.valid ? "CHAIN VERIFIED" : "CHAIN BROKEN"} · head {chain.head_hash.slice(0, 20)}…
        </div>
      )}
      <ol className="relative before:absolute before:left-[13px] before:top-2 before:bottom-4 before:w-px before:bg-[#1D2826]">
        {evidence.map((e) => <Node key={e.id} e={e} />)}
      </ol>
    </div>
  );
};
